import { EventEmitter, inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import Keyboard, { KeyboardLayoutObject } from 'simple-keyboard';
import { CatalogEnvService } from '@aipb/gulliver/catalog/env';

@Injectable()
export class SearchKeyboardService {
  private _identifier: string;
  private _lastLayout: string;
  private _change: EventEmitter<string> = new EventEmitter();
  public change$ = this._change.asObservable();
  private transloco = inject(TranslocoService);
  private catalogEnv = inject(CatalogEnvService);
  private _keyboardLayoutNames: string[] = [];

  private _keyboard: Keyboard;

  get keyboard(): Keyboard | null {
    return this._keyboard;
  }

  initKeyboard(selector: string, onChange: (input: string) => void) {
    const layout: KeyboardLayoutObject = {};

    const keyboardLayout = this.catalogEnv.env.virtualKeyboard.layout;
    this._keyboardLayoutNames = Object.keys(keyboardLayout).filter((n) => n !== 'default');

    Object.keys(keyboardLayout).forEach((key) => {
      layout[key] = [
        this._keyboardLayoutNames.map((n) => `{${n}}`).join(' '),
        ...this.chunk(keyboardLayout[key], 8).map((d) => d.join(' '))
      ];
    });

    const buttonThemes: {
      class: string;
      buttons: string;
    }[] = [];

    (this.catalogEnv.env.virtualKeyboard.buttonThemes || []).forEach((buttonTheme) => {
      let buttons = '';

      if (buttonTheme.layouts) {
        buttons = buttonTheme.layouts.map((l) => layout[l].join(' ')).join(' ');
      } else if (buttonTheme.buttons) {
        buttons = buttonTheme.buttons.join(' ');
      }

      buttonThemes.push({
        class: buttonTheme.class,
        buttons
      });
    });

    const display: Record<string, string> = {};

    const labels = this.catalogEnv.env.virtualKeyboard.labels || {};
    Object.keys(labels).forEach((key) => {
      display[key] = this.transloco.translate(labels[key]);
    });

    this._keyboard = new Keyboard(selector, {
      theme: 'simple-keyboard hg-theme-default hg-layout-default',
      onChange: (input) => onChange(input),
      onKeyPress: (button) => this.onKeyPress(button),
      disableButtonHold: true,
      layout: layout,
      buttonTheme: buttonThemes,
      display: display
    });

    return this._keyboard;
  }

  clear(): void {
    if (!this._keyboard) {
      return;
    }

    this._keyboard.clearInput();
  }

  setInput(value: string): void {
    if (!this._keyboard) {
      return;
    }

    this._keyboard.setInput(value);
    this._keyboard.caretPosition = null;
  }

  onKeyPress = (button: string) => {
    this._keyboardLayoutNames.forEach((layoutName) => {
      if (`{${layoutName}}` === button) {
        this.changeLayout(layoutName);
      }
    });
  };

  changeLayout(layout: string): void {
    this._keyboard.setOptions({
      layoutName: layout
    });
  }

  chunk(arr: string[], chunkSize: number): string[][] {
    const R = [];
    for (let i = 0; i < arr.length; i += chunkSize) {
      R.push(arr.slice(i, i + chunkSize));
    }
    return R;
  }
}
